// Resize-up flow. Persistent disks can only grow — the new size must be
// strictly greater than the current size (EBS volumes can't shrink). On submit
// we dispatch a `modify` operation with the new size.
import {
  estimateMonthlyCost,
  formatCurrency,
  modifyPersistentDisk,
} from "@app/deploy";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import { environmentActivityUrl } from "@app/routes";
import { schema } from "@app/schema";
import type { DeployPersistentDisk } from "@app/types";
import { useValidator } from "@app/ui/hooks";
import { BannerMessages } from "@app/ui/shared/banner";
import { Button } from "@app/ui/shared/button";
import { Form, FormGroup } from "@app/ui/shared/form-group";
import { IconCylinder } from "@app/ui/shared/icons";
import { Input } from "@app/ui/shared/input";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DiskRestartWarning } from "./disk-restart-warning";

interface ResizeFormData {
  newSize: number;
  currentSize: number;
}

const buildResizeValidators = () => ({
  // Up-only: the new size must exceed the current size. Disks cannot shrink.
  newSize: (data: ResizeFormData) => {
    if (!Number.isFinite(data.newSize) || data.newSize <= 0) {
      return "Size must be a positive number of GB";
    }
    if (data.newSize <= data.currentSize) {
      return `New size must be greater than the current size (${data.currentSize} GB)`;
    }
  },
});

interface ResizePersistentDiskModalProps {
  isOpen: boolean;
  disk: DeployPersistentDisk;
  onClose: () => void;
}

export const ResizePersistentDiskModal = ({
  isOpen,
  disk,
  onClose,
}: ResizePersistentDiskModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Seed one GB above the current size so the form starts in a valid state.
  // Stored as a raw string so the user can clear and free-type a new value
  // (e.g., 015 -> 15) without leading zeros being prepended.
  const initialSize = String(disk.size + 1);
  const [sizeInput, setSizeInput] = useState(initialSize);
  const newSize = sizeInput.trim() === "" ? Number.NaN : Number(sizeInput);

  const validators = buildResizeValidators();
  const [errors, validate] = useValidator<ResizeFormData, typeof validators>(
    validators,
  );

  const loader = useLoader(modifyPersistentDisk);

  // Est. cost line reuses the same persistent_disk_cost_gb_per_month rate
  // basis as the create modal and the list table. Falls back to the current
  // disk size while the user is editing the field so the line stays present
  // rather than flashing NaN.
  const rates = useSelector(schema.costRates.select);
  const estCost = estimateMonthlyCost({
    rates,
    persistentDisks: [
      {
        size: Number.isFinite(newSize) ? newSize : disk.size,
        provisionedIops: 0,
      },
    ],
  });

  const handleClose = () => {
    setSizeInput(initialSize);
    onClose();
  };

  useLoaderSuccess(loader, () => {
    // TODO: use appActivityUrl once operations properly populate targetResources
    // with the service and app being resized.
    navigate(environmentActivityUrl(disk.environmentId));
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: ResizeFormData = { newSize, currentSize: disk.size };
    if (!validate(data)) return;
    dispatch(modifyPersistentDisk({ id: disk.id, size: newSize }));
  };

  if (!isOpen) return null;

  // Up-only guard, surfaced live (mirrors the attach modal's eager warning).
  // `errors.newSize` (from the submit-time validator) takes precedence so a
  // submit attempt still shows its message. A blank field is allowed at rest
  // so the user can clear the input and re-type freely; the submit button is
  // disabled until the value resolves to a strictly larger size.
  const liveSizeError =
    Number.isFinite(newSize) && newSize <= disk.size
      ? `New size must be greater than the current size (${disk.size} GB)`
      : undefined;
  const sizeError = errors.newSize ?? liveSizeError;
  const submitDisabled = !Number.isFinite(newSize) || newSize <= disk.size;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={handleClose} className="min-w-[600px]">
        <ModalHeading
          icon={<IconCylinder variant="sm" />}
          title={`Resize Disk: ${disk.handle}`}
          onClose={handleClose}
        />
        <Form onSubmit={handleSubmit}>
          <ModalBody className="pb-0">
            <DiskRestartWarning className="mb-4" />
            <p className="mb-4 text-gray-700">
              Current size: <strong>{disk.size} GB</strong>. Disks can only be
              resized up.
            </p>
            <FormGroup
              label={
                <>
                  New Size (GB) <span className="text-red-500">*</span>
                </>
              }
              htmlFor="resize-disk-size"
              className="mb-2"
              feedbackMessage={sizeError}
              feedbackVariant={sizeError ? "danger" : "default"}
            >
              <Input
                id="resize-disk-size"
                name="newSize"
                type="number"
                min={disk.size + 1}
                value={sizeInput}
                onChange={(e) => setSizeInput(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                You can increase disk size at any time, but cannot decrease it.
              </p>
            </FormGroup>

            <p className="text-gray-500 text-sm">
              Est. cost: {formatCurrency(estCost)}/month
            </p>

            <BannerMessages className="mt-4" {...loader} />
          </ModalBody>
          <ModalFooter
            actions={[
              <Button
                key="submit"
                type="submit"
                disabled={submitDisabled}
                isLoading={loader.isLoading}
              >
                Resize Disk
              </Button>,
            ]}
          />
        </Form>
      </Modal>
    </ModalWrapper>
  );
};
